import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Trash2 } from "lucide-react";
import { useStore } from "../../lib/store";
import { showFeedback } from "../../lib/feedback";
import type { Priority, Task, TaskProgress } from "../../lib/types";
import { TASK_COLUMNS, taskColumn, type TaskColumnId } from "./board";
import { localParts, toInstant } from "./civil-time";
import { PrioritySegments } from "../leads/LeadFormFields";
import { Button } from "../../ui/Button";
import { IconButton } from "../../ui/IconButton";
import { Drawer } from "../../ui/Dialog";
import { Field, TextArea, TextInput } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { ConfirmDialog } from "../../pages/PerfilDialogs";

/**
 * Criação e edição de tarefa, compartilhadas pelo quadro de Tarefas, pelo
 * perfil do lead, pelo acesso rápido e pelas conversas.
 *
 * É a gaveta do protótipo (`zTaskForm`): a próxima ação, o cadastro, o
 * andamento, data e horário, a prioridade em três botões e as observações. Os
 * caminhos usam o mesmo schema, os mesmos defaults e as mesmas mensagens; o
 * que muda é só de onde vem o cadastro (AGENTS §7.5, §14.1).
 *
 * Sem contexto de lead, o vínculo é opcional: tarefa interna não inventa um
 * lead só para caber no formulário (decisão do Zênite, §24). Na edição o
 * cadastro não muda — a jornada dele já registra esta tarefa, e trocá-lo
 * reescreveria o passado (§7.4) —, então o campo aparece só para leitura.
 *
 * O que foi digitado fica como rascunho enquanto a sessão durar: fechar a
 * gaveta não perde nada, e "Descartar rascunho" é a forma explícita de
 * jogar fora (§26.2). O rascunho mora em memória, não no navegador.
 */

/** Valor do seletor para a tarefa que não pertence a um cadastro. */
const INTERNAL = "__interna__";

interface TaskDraft {
  leadId: string;
  description: string;
  column: TaskColumnId;
  date: string;
  time: string;
  priority: Priority;
  note: string;
}

/** Rascunhos por formulário, vivos até salvar ou descartar. */
const DRAFTS = new Map<string, TaskDraft>();

export function resetTaskDraftsForTests() {
  DRAFTS.clear();
}

export function NewTaskDialog({
  open,
  progress,
  fixedLeadId,
  task,
  onClose,
  onCreated,
}: {
  open: boolean;
  progress: TaskProgress;
  /** Lead já definido pelo contexto; quando existe, não há seletor. */
  fixedLeadId?: string;
  /** Tarefa existente: a gaveta vira edição. */
  task?: Task;
  onClose: () => void;
  onCreated?: (taskId: string) => void;
}) {
  const store = useStore();
  const leads = useMemo(
    () => store.leads.filter((lead) => !lead.archived && !lead.mergedIntoId),
    [store.leads],
  );
  const fixedLead = fixedLeadId ? store.leads.find((lead) => lead.id === fixedLeadId) : undefined;
  const taskLead = task?.leadId ? store.leads.find((lead) => lead.id === task.leadId) : undefined;
  const draftKey = task ? `edit:${task.id}` : `new:${fixedLeadId ?? ""}:${progress}`;

  const initial = useMemo<TaskDraft>(() => {
    const target = task ? new Date(task.dueAt) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    const parts = localParts(target);
    return {
      leadId: task?.leadId ?? fixedLeadId ?? INTERNAL,
      description: task?.description ?? "",
      column: task ? taskColumn(task) : progress,
      date: parts.date,
      time: task ? parts.time : "09:00",
      priority: task?.priority ?? "medium",
      note: task?.note ?? "",
    };
  }, [fixedLeadId, progress, task]);

  const [form, setForm] = useState<TaskDraft>(initial);
  const [recovered, setRecovered] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const draft = DRAFTS.get(draftKey);
    setForm(draft ?? initial);
    setRecovered(Boolean(draft));
    setErrors({});
    setSaving(false);
  }, [draftKey, initial, open]);

  const update = (patch: Partial<TaskDraft>) => {
    setForm((current) => {
      const next = { ...current, ...patch };
      DRAFTS.set(draftKey, next);
      return next;
    });
  };

  const discard = () => {
    DRAFTS.delete(draftKey);
    onClose();
    showFeedback("Rascunho descartado.", "success");
  };

  /** Andamento escolhido na gaveta, pelo mesmo caminho do arraste no quadro. */
  const moveTo = async (current: Task, target: TaskColumnId) => {
    if (taskColumn(current) === target) return true;
    if (target === "done") return (await store.completeTask(current.id)).ok;
    if (current.status === "done") {
      const reopened = await store.reopenTask(current.id);
      if (!reopened.ok) return false;
      if (reopened.value.task.progress === target) return true;
      return (await store.setTaskProgress(current.id, target)).ok;
    }
    return (await store.setTaskProgress(current.id, target)).ok;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const next: Record<string, string> = {};
    if (form.description.trim() === "") next.description = "Descreva a ação.";
    const dueAt = toInstant(form.date, form.time);
    if (!dueAt) next.due = "Informe uma data e um horário válidos.";
    setErrors(next);
    if (Object.keys(next).length > 0) {
      if (next.description) firstRef.current?.focus();
      return;
    }

    setSaving(true);
    const note = form.note.trim() === "" ? undefined : form.note.trim();
    if (task) {
      const contentChanged =
        form.description.trim() !== task.description ||
        (note ?? "") !== (task.note ?? "") ||
        form.priority !== task.priority ||
        dueAt !== task.dueAt;
      let current = task;
      if (contentChanged) {
        const edited = await store.updateTask({
          taskId: task.id,
          description: form.description.trim(),
          dueAt: dueAt as string,
          priority: form.priority,
          note,
        });
        if (!edited.ok) {
          setSaving(false);
          return;
        }
        current = edited.value.task;
      }
      const moved = await moveTo(current, form.column);
      setSaving(false);
      if (!moved) return;
      DRAFTS.delete(draftKey);
      showFeedback("Tarefa salva.", "success");
      onClose();
      return;
    }
    const created = await store.addTask({
      leadId: form.leadId === INTERNAL ? undefined : form.leadId,
      description: form.description.trim(),
      dueAt: dueAt as string,
      priority: form.priority,
      note,
      progress: form.column === "done" ? "todo" : form.column,
    });
    if (!created.ok) {
      setSaving(false);
      return;
    }
    if (form.column === "done") await store.completeTask(created.value.task.id);
    setSaving(false);
    DRAFTS.delete(draftKey);
    onCreated?.(created.value.task.id);
    showFeedback("Tarefa criada.", "success");
    onClose();
  };

  const linkedLead = task ? taskLead : fixedLead;
  const linkedLabel = task
    ? taskLead
      ? `${taskLead.name} · ${taskLead.company}`
      : task.leadId
        ? "Cadastro indisponível — vínculo preservado"
        : "Tarefa interna"
    : fixedLead
      ? `${fixedLead.name} · ${fixedLead.company}`
      : "";

  return (
    <>
      <Drawer
        open={open && !deleting}
        onClose={onClose}
        title={task ? "Editar tarefa" : "Nova tarefa"}
        initialFocusRef={firstRef}
        className="cx-task-form"
        headerActions={
          task ? (
            <IconButton
              label="Excluir tarefa"
              icon={<Trash2 size={15} aria-hidden="true" />}
              onClick={() => setDeleting(true)}
            />
          ) : undefined
        }
        footer={
          <>
            <Button variant="ghost" className="cx-task-discard" onClick={discard}>
              Descartar rascunho
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
            <Button variant="primary" form="nova-tarefa" type="submit" loading={saving}>
              {task ? "Salvar tarefa" : "Criar tarefa"}
            </Button>
          </>
        }
      >
        <form id="nova-tarefa" className="cx-form" onSubmit={submit} noValidate>
          <p className="cx-form-caption">Defina a próxima ação, seu prazo e o cadastro relacionado.</p>
          <Field label="O que precisa ser feito?" required error={errors.description}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                ref={firstRef}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                placeholder="Ex.: Enviar proposta revisada"
                value={form.description}
                maxLength={500}
                onChange={(event) => update({ description: event.target.value })}
              />
            )}
          </Field>
          <Field label="Cadastro">
            {({ id, describedBy }) =>
              linkedLead || task ? (
                <TextInput id={id} aria-describedby={describedBy} value={linkedLabel} readOnly aria-readonly="true" />
              ) : (
                <Select
                  id={id}
                  label="Cadastro"
                  describedBy={describedBy}
                  size="field"
                  value={form.leadId}
                  onChange={(value) => update({ leadId: value })}
                  options={[
                    { value: INTERNAL, label: "Tarefa interna", hint: "Trabalho da operação" },
                    ...leads.map((lead) => ({ value: lead.id, label: lead.name, hint: lead.company })),
                  ]}
                />
              )
            }
          </Field>
          <Field label="Andamento">
            {({ id, describedBy }) => (
              <Select
                id={id}
                label="Andamento"
                describedBy={describedBy}
                size="field"
                value={form.column}
                onChange={(value) => update({ column: value as TaskColumnId })}
                options={TASK_COLUMNS.map((column) => ({ value: column.id, label: column.label }))}
              />
            )}
          </Field>
          <div className="cx-form-row">
            <Field label="Data" required error={errors.due}>
              {({ id, describedBy, invalid }) => (
                <TextInput
                  id={id}
                  type="date"
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={form.date}
                  onChange={(event) => update({ date: event.target.value })}
                />
              )}
            </Field>
            <Field label="Horário" required>
              {({ id }) => (
                <TextInput id={id} type="time" value={form.time} onChange={(event) => update({ time: event.target.value })} />
              )}
            </Field>
          </div>
          <PrioritySegments value={form.priority} onChange={(priority) => update({ priority })} />
          <Field label="Observações">
            {({ id }) => (
              <TextArea id={id} rows={3} value={form.note} maxLength={2000} onChange={(event) => update({ note: event.target.value })} />
            )}
          </Field>
          <p className="cx-draft-note">
            {recovered ? "Rascunho recuperado. Revise e salve para aplicar." : "Sua edição fica como rascunho até salvar."}
          </p>
        </form>
      </Drawer>
      {deleting && task && (
        <ConfirmDialog
          title="Excluir tarefa"
          question={`“${task.description}” sai do quadro e da lista.${task.leadId ? " A jornada do cadastro registra a exclusão." : ""}`}
          confirmLabel="Excluir tarefa"
          danger
          onConfirm={() => {
            setDeleting(false);
            void store.deleteTask(task.id).then((result) => {
              if (!result.ok) return;
              DRAFTS.delete(draftKey);
              showFeedback("Tarefa excluída.", "success");
              onClose();
            });
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </>
  );
}
